import { useMemo } from 'react';

import { STORAGE_KEYS } from '@constants/storage';
import { getLocalStorageItem } from '@utils/storage';
import { BarLineComboChartSeries } from 'types/e-charts';
import { DateParams } from 'types/lead-management';

import { LeadAnalyticsCriteria } from '../constants/leadAnalytics';
import { buildDateRangeQuery } from '../helper';
import {
  useGetAgentsStatusSummaryQuery,
  useGetLeadAnalyticsConversionQuery,
  useGetLeadAnalyticsLocationsQuery,
  useGetLeadAnalyticsSourceQuery
} from '../queries/LeadManagementQuery';
import {
  LeadAnalyticsData,
  LeadSource,
  LeadStatusSummaryRow
} from '../types/leadAnalytics';
import {
  getPaletteColor,
  REVENUE_LINE_COLOR,
  STATUS_PALETTE
} from '../utils/analyticsColors';
import useErrorToast from './useErrorToast';
import useLeadAgentRestrictions from './useLeadAgentRestrictions';
import useLeadSources from './useLeadSources';

const useLeadAnalyticsData = (
  params: DateParams,
  locationView: LeadAnalyticsCriteria,
  sourceView: LeadAnalyticsCriteria
): LeadAnalyticsData => {
  const organisationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { getSourceLabel, getSourceColor } = useLeadSources();
  const { isSalesAgent } = useLeadAgentRestrictions();

  const conversion = useGetLeadAnalyticsConversionQuery({
    organisationId,
    queryString: buildDateRangeQuery(params)
  });
  const source = useGetLeadAnalyticsSourceQuery({
    organisationId,
    queryString: buildDateRangeQuery(params, { criteria: sourceView })
  });
  const locations = useGetLeadAnalyticsLocationsQuery({
    organisationId,
    queryString: buildDateRangeQuery(params, { view: locationView })
  });
  // Sales agents don't see the per-agent distribution table, so skip its fetch.
  const agents = useGetAgentsStatusSummaryQuery(
    {
      organisationId,
      queryString: buildDateRangeQuery(params)
    },
    { skip: isSalesAgent }
  );

  const statusSeries = useMemo<BarLineComboChartSeries[]>(() => {
    const statuses = [...(conversion.data?.statuses ?? [])].sort(
      (a, b) => a.order - b.order
    );
    if (!statuses.length) return [];
    const bars: BarLineComboChartSeries[] = statuses.map((status, index) => ({
      name: status.display_name,
      type: 'bar',
      color: status.color || getPaletteColor(STATUS_PALETTE, index),
      order: status.order,
      data: statuses.map((_, col) => (col === index ? status.lead_count : 0))
    }));
    return [
      ...bars,
      {
        name: 'Revenue',
        type: 'line',
        color: REVENUE_LINE_COLOR,
        data: statuses.map((status) => status.revenue)
      }
    ];
  }, [conversion.data]);

  const sourceItems = useMemo<LeadSource[]>(
    () =>
      (source.data?.sources ?? []).map((item) => ({
        label: getSourceLabel(item.name) || item.name,
        value: item.percentage ?? 0,
        count: item.count,
        color: getSourceColor(item.name)
      })),
    [source.data, getSourceLabel, getSourceColor]
  );

  const { agentRows, agentStatusList } = useMemo(() => {
    const headers = agents.data?.headers ?? [];
    const statusList = headers.map((header) => ({
      id: header.status_id,
      name: header.display_name,
      color: header.color
    }));
    const nameByStatusId = new Map(
      headers.map((header) => [header.status_id, header.display_name])
    );
    const rows: LeadStatusSummaryRow[] = (agents.data?.rows ?? []).map(
      (row, index) => {
        const count: Record<string, number> = {};
        const revenue: Record<string, number> = {};
        row.by_status.forEach((entry) => {
          const statusName = nameByStatusId.get(entry.status_id);
          if (statusName) {
            count[statusName] = entry.count;
            revenue[statusName] = entry.revenue;
          }
        });
        return {
          id: `${row.agent_name}-${index}`,
          label: row.agent_name,
          count,
          revenue,
          totalCount: row.total_count,
          totalRevenue: row.total_revenue
        };
      }
    );
    return { agentRows: rows, agentStatusList: statusList };
  }, [agents.data]);

  const hasError =
    conversion.isError || source.isError || locations.isError || agents.isError;

  useErrorToast(hasError);

  return {
    statusSeries,
    sourceItems,
    locations: locations.data?.locations ?? [],
    agentRows,
    agentStatusList,
    isSalesAgent,
    loading: {
      statusChart: conversion.isLoading,
      sources: source.isLoading,
      locations: locations.isLoading,
      agents: agents.isLoading
    }
  };
};

export default useLeadAnalyticsData;
